import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { CmsEntities } from 'client/data/models/cms';
import { parseDataPointsMetadata, parseLinkMetadata } from 'site-modules/shared/utils/parse-content-metadata';
import { CONTENT_COLLAPSE_STATE, DATA_POINTS_POSITION } from 'site-modules/editorial/constants/best';
import { CardButtons } from './card-buttons';
import { CardPhoto } from './card-photo';
import { CardContent } from './card-content';
import { DataPoints } from './data-points';

import './vehicle-card.scss';

export function VehicleCard({
  entry,
  selectedCars,
  onSelectCar,
  number = null,
  isMobile = false,
  componentToUse = 'div',
  index = 0,
}) {
  // aria-id
  const ariaId = `${entry.parentId}-${entry.id}-${index}`;

  const rank = entry.metadata('ranking').value();

  // data points
  const dataPointsList = parseDataPointsMetadata(entry.child('vehicle-data-points'));
  const hasDataPoints = !!dataPointsList.length;

  // truncate options
  const contentCollapseState = entry.metadata('card-display').value(CONTENT_COLLAPSE_STATE.NONE);
  const dataPointsPosition = entry.metadata('card-display-data').value(DATA_POINTS_POSITION.TOP);
  const isTopDataPoints = dataPointsPosition === DATA_POINTS_POSITION.TOP;

  // photo data
  const photoEntry = entry.child('photo');
  const vehicleParams = photoEntry.vehicles[0];
  const photoLinks = photoEntry.links();

  // title
  const titleEntry = entry.child('vehicle-title');
  const titleLink = parseLinkMetadata(titleEntry, 'vehicle');
  const subtitle = titleEntry.metadata('vehicle-subtitle').value();

  // content
  const contentEntry = entry.child('vehicle-content');
  const content = contentEntry.metadata('text').value();

  // buttons
  const buttonsEntry = entry.child('card-buttons');
  const primaryLink = parseLinkMetadata(buttonsEntry, 'button2');
  const secondaryLink = parseLinkMetadata(buttonsEntry, 'button1');
  const Tag = componentToUse;

  return (
    <Tag className="vehicle-card card mb-1">
      <Row>
        <Col xs={12} md={3} className="pe-md-0 pb-1">
          <CardPhoto
            vehicleParams={vehicleParams}
            photoLinks={photoLinks}
            titleLink={titleLink}
            subtitle={subtitle}
            selectedCars={selectedCars}
            number={number}
            onSelectCar={onSelectCar}
            isMobile={isMobile}
            ariaId={ariaId}
          />
        </Col>
        {hasDataPoints && (
          <Col
            xs={12}
            md={3}
            className={classnames('px-1_5 ps-md-0_5 pe-md-0_5 order-md-last', {
              'order-last bottom mt-1 mt-md-0': !isTopDataPoints,
            })}
          >
            <DataPoints list={dataPointsList} keyPrefix={vehicleParams.style} />
          </Col>
        )}
        <Col xs={12} md={hasDataPoints ? 6 : 9} className="px-1_5 ps-md-1 pe-md-0_5">
          <CardContent
            titleLink={titleLink}
            subtitle={subtitle}
            content={content}
            contentCollapseState={contentCollapseState}
            primaryLink={primaryLink}
            secondaryLink={secondaryLink}
            number={number}
            isTopDataPoints={isTopDataPoints}
            isMobile={isMobile}
            ariaId={ariaId}
          />
        </Col>
        {isMobile && (
          <Col xs={12} className="order-last px-1_5">
            <CardButtons primaryLink={primaryLink} secondaryLink={secondaryLink} ariaId={ariaId} />
          </Col>
        )}
      </Row>
      {!!rank && <div className="rank rounded heading-4 bg-gray-extra-light px-0_5 py-0_25">{rank}</div>}
    </Tag>
  );
}

VehicleCard.propTypes = {
  entry: CmsEntities.Content.isRequired,
  selectedCars: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectCar: PropTypes.func.isRequired,
  number: PropTypes.number,
  isMobile: PropTypes.bool,
  componentToUse: PropTypes.string,
  index: PropTypes.number,
};
